/* Products — grocery catalog. Backed by T-201. */

import { api, mockOr } from '../lib/api.js';
import { D } from '../mock-data.js';

export async function listProducts(branchId, { category, cursor, limit = 20 } = {}) {
  return mockOr(
    () => api.get(`/branches/${branchId}/products`, { query: { category, cursor, limit } }),
    () => {
      const b = D.branches.grocery.find((x) => x.id === branchId);
      if (!b) return { items: [], nextCursor: null };
      const all = category ? b.products.filter((p) => p.cat === category) : b.products;
      const start = cursor ? parseInt(cursor, 10) || 0 : 0;
      const slice = all.slice(start, start + limit);
      const nextCursor = start + limit < all.length ? String(start + limit) : null;
      return { items: slice, nextCursor };
    },
  );
}

export async function getProduct(productId) {
  return mockOr(
    () => api.get(`/products/${productId}`),
    () => {
      for (const b of D.branches.grocery) {
        const p = b.products.find((x) => x.id === productId);
        if (p) return { ...p, meta: D.productMeta[productId] || null };
      }
      throw new Error('product not found');
    },
  );
}

/* listSubscribableProducts — flat cross-branch product list with vendor
   attribution for the new ProductCatalogScreen. Filters by category and
   cadence support, paginated. */
export async function listSubscribableProducts({ category, cadence, cursor, limit = 20 } = {}) {
  return mockOr(
    () => api.get('/products/subscribable', { query: { category, cadence, cursor, limit } }),
    () => {
      const flat = [];
      for (const b of D.branches.grocery) {
        for (const p of b.products) {
          if (!p.subscribable) continue;
          if (category && p.cat !== category) continue;
          if (cadence && Array.isArray(p.cadences) && !p.cadences.includes(cadence)) continue;
          flat.push({
            ...p,
            branchId: b.id,
            branchName: b.name,
            branchRating: b.rating,
            branchDist: b.dist,
            branchEm: b.em,
          });
        }
      }
      flat.sort((a, b) => (new Date(b.createdAt || 0)) - (new Date(a.createdAt || 0)));
      const start = cursor ? parseInt(cursor, 10) || 0 : 0;
      const items = flat.slice(start, start + limit);
      const nextCursor = start + limit < flat.length ? String(start + limit) : null;
      return { items, nextCursor };
    },
  );
}

/* listNewProducts — recently-launched subscribable products for the
   "New on Rekraft" rail. Sorted by createdAt desc, capped by sinceDays. */
export async function listNewProducts({ category, limit = 10, sinceDays = 14 } = {}) {
  return mockOr(
    () => api.get('/products/new', { query: { category, limit, sinceDays } }),
    () => {
      const cutoff = Date.now() - sinceDays * 24 * 60 * 60 * 1000;
      const flat = [];
      for (const b of D.branches.grocery) {
        for (const p of b.products) {
          if (!p.subscribable) continue;
          if (category && p.cat !== category) continue;
          if (!p.createdAt) continue;
          if (new Date(p.createdAt).getTime() < cutoff) continue;
          flat.push({
            ...p,
            branchId: b.id,
            branchName: b.name,
            branchRating: b.rating,
            branchEm: b.em,
          });
        }
      }
      flat.sort((a, b) => (new Date(b.createdAt)) - (new Date(a.createdAt)));
      return flat.slice(0, limit);
    },
  );
}
